import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconDelete, IconEdit, IconTrendComment } from 'svgIcons/MotionPortalIcons';
import { translate } from 'helpers/translateHelper';
import { convertUtcToLocale, formatDateTime } from 'helpers/dateTimeFormatter';
import { formatKpiLabel, splitSelectedSignals } from './helpers';

const TrendCommentsList = (props) => {
    const {
        trendCommentsList,
        setIsShowModal,
        setDeleteCommentId,
        trendKPIsList
    } = props;

    // State to track expanded comments
    const [expandedComments, setExpandedComments] = useState(new Set());

    // Function to toggle comment expansion
    const toggleCommentExpansion = (commentId) => {
        setExpandedComments(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(commentId)) {
                newExpanded.delete(commentId);
            } else {
                newExpanded.add(commentId);
            }
            return newExpanded;
        });
    };

    // Function to check if text needs truncation (more than 2 lines)
    const needsTruncation = (text, maxLength = 110) => {
        return text && text.length > maxLength;
    };

    // Function to get truncated text (approximately 2 lines)
    const getTruncatedText = (text, maxLength = 110) => {
        if (!text) {
            return '';
        }
        if (text.length <= maxLength) {
            return text;
        }

        // Find the last space before maxLength to avoid cutting words
        const truncated = text.substring(0, maxLength);
        const lastSpaceIndex = truncated.lastIndexOf(' ');

        return lastSpaceIndex > 0 ?
            truncated.substring(0, lastSpaceIndex) :
            truncated;
    };

    const getSignalLabels = (timeseriesKeyString) => {
        const kpiByKey = new Map(
            (trendKPIsList || []).map(kpi => [kpi.timeseriesKey, kpi])
        );
        const keys = splitSelectedSignals(timeseriesKeyString);
        if (!keys.length) {
            return '';
        }

        return keys
            .map((key) => {
                const kpi = kpiByKey.get(key);
                return kpi ? formatKpiLabel(kpi) : key;
            })
            .join(', ');
    };

    const formatUtcToLocal = (value) => {
        const m = convertUtcToLocale(value);
        return m ? formatDateTime(m) : '';
    };

    return (
        <div className='trend-comments-list'>
            {trendCommentsList.map((comment) => {
                const isExpanded = expandedComments.has(comment.id);
                const showTruncation = needsTruncation(comment.commentText);
                const kpiNames = getSignalLabels(comment.timeseriesKey);

                return (
                    <div key={comment.id} className='trend-comment-item'>
                        <div className='comment-subject'>
                            <IconTrendComment />
                            <div className='subject-content'>
                                <span>{comment.subjectText}</span>
                                <div className='comment-actions'>
                                    {comment.canEdit &&
                                        <IconEdit
                                            width={15}
                                            height={15}
                                            onClick={() => setIsShowModal(true, comment)}
                                        />
                                    }
                                    {comment.canDelete &&
                                        <IconDelete
                                            onClick={() => setDeleteCommentId(comment.id)}
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='comment-content'>
                            <div className='title'>
                                <span>
                                    {translate('ABB.Powertrain.Frontend.opTrendCommentsTime')}:
                                </span>
                                {formatUtcToLocal(comment.timestamp)}
                            </div>
                            <div className='title'>
                                <span>
                                    {translate('ABB.Powertrain.Frontend.opTrendCommentsRelevantSignals')}:
                                </span>
                                {kpiNames}
                            </div>
                            <div className='comment-timestamp'>
                                <span className='title'>
                                    {translate('ABB.Powertrain.Frontend.opTrendCommentsModalAcceptBtn')}
                                </span>
                                <span className='timestamp'>
                                    {formatUtcToLocal(comment.createdTimestamp)}
                                </span>
                            </div>

                            {/* Expandable comment text */}
                            <div className='comment-text-container'>
                                <p className='comment-text'>
                                    {showTruncation && !isExpanded
                                        ? getTruncatedText(comment.commentText)
                                        : comment.commentText
                                    }
                                </p>

                                {showTruncation &&
                                    <button
                                        type='button'
                                        className='toggle-comment-btn'
                                        onClick={() => toggleCommentExpansion(comment.id)}
                                    >
                                        {isExpanded
                                            ? translate('ABB.Powertrain.Frontend.opTrendCommentsShowLess')
                                            : translate('ABB.Powertrain.Frontend.opTrendCommentsShowMore')
                                        }
                                    </button>
                                }
                            </div>

                            {comment.internal &&
                                <div className='user-email'>
                                    <span>
                                        {comment.author ?? ''}
                                    </span>
                                    <span className='internal-label'>
                                        {translate('ABB.Powertrain.Frontend.opTrendCommentsInternalLabel')}
                                    </span>
                                </div>
                            }
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

TrendCommentsList.propTypes = {
    trendCommentsList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        subjectText: PropTypes.string.isRequired,
        commentText: PropTypes.string,
        timestamp: PropTypes.string,
        timeseriesKey: PropTypes.string,
        canEdit: PropTypes.bool,
        canDelete: PropTypes.bool,
        internal: PropTypes.bool,
        author: PropTypes.string,
        createdTimestamp: PropTypes.string
    })).isRequired,
    setIsShowModal: PropTypes.func.isRequired,
    setDeleteCommentId: PropTypes.func.isRequired,
    trendKPIsList: PropTypes.arrayOf(PropTypes.shape({
        timeseriesKey: PropTypes.string.isRequired,
        timeseriesName: PropTypes.string.isRequired
    }))
};

export default TrendCommentsList;
